import React from 'react';
import { inject, observer } from 'mobx-react';
import { reaction, IReactionDisposer } from 'mobx';
import type { AppStore } from '@stores/app/store';
import type { SiteAction } from '@stores/site/action';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps, BasePageState } from '../../../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import Toast from '@components/Toast';
import TiptapEditor from './components/TiptapEditor';
import FeedbackFormFields from '../../components/FeedbackFormFields/pc';
import FeedbackAttachmentDisplay from '../../components/FeedbackAttachmentUpload/pc';
import FeedbackAttachmentProvider from '../../components/FeedbackAttachmentUpload/FeedbackAttachmentProvider';
import './index.scss';
import { extractMentionsFromHtml } from '../../../../utils/setPostMentions';
import { isSameEditorContent } from '../../../../utils/tiptapEditorContent';

interface SetFeedbackPostPCPageProps extends BasePageProps, RouteComponentProps {
    app?: AppStore;
    site?: SiteAction;
    user?: UserAction;
    thread?: ThreadAction;
    setPost?: any;
    setPostData: (data: any) => void;
    onPublishPost: (options?: { onSuccess?: (payload: { threadId?: string | number; result: any; isEdit: boolean }) => void }, coverFile?: File | null, videoFile?: File | null) => void;
    loading?: boolean;
    editorReady?: boolean;
}
interface SetFeedbackPostPCState extends BasePageState {
    title: string;
    hasDraft: boolean;
    restoreDraftLoading: boolean;
    saveDraftLoading: boolean;
    editorInstanceKey: number;
}

@inject('app', 'site', 'user', 'thread', 'setPost')
@observer
class SetFeedbackPostPC extends BasePage<SetFeedbackPostPCPageProps, SetFeedbackPostPCState> {
    titleDisposer: IReactionDisposer | null = null;
    problemSolvedDisposer: IReactionDisposer | null = null;

    state: SetFeedbackPostPCState = {
        loading: false,
        error: null,
        pageLoaded: false,
        title: '',
        hasDraft: false,
        restoreDraftLoading: false,
        saveDraftLoading: false,
        editorInstanceKey: 0,
    };
    componentDidMount() {
        this.onPageMount();
        const store = this.props.setPost;
        if (store?.postData && store.postData.type !== 2) {
            this.props.setPostData({ type: 2 });
        }
        if (store?.postData) {
            this.setState({ title: store.postData.title || '' });

            this.titleDisposer = reaction(
                () => store.postData?.title,
                (newTitle) => {
                    this.setState({ title: newTitle || '' });
                }
            );
            this.problemSolvedDisposer = reaction(
                () => ({
                    problemId: String(store.postData?.problemId || ''),
                    solvedCount: Number(store.postData?.problemSolvedCount ?? 0) || 0,
                    countLoaded: !!store.postData?.problemSolvedCountLoaded,
                }),
                ({ problemId, solvedCount, countLoaded }) => {
                    if (problemId && countLoaded && solvedCount <= 0) {
                        Toast.error('暂未找到相关已解决问题帖');
                    }
                }
            );
        }

        this.checkDraft();
    }

    componentWillUnmount() {
        if (this.titleDisposer) {
            this.titleDisposer();
        }
        if (this.problemSolvedDisposer) {
            this.problemSolvedDisposer();
        }
    }

    handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const title = e.target.value;
        this.setState({ title });
        this.props.setPostData({ title });
    }

    handleContentChange = (content: string) => {
        const prevContent = this.props.setPost?.postData?.content || '';
        if (isSameEditorContent(prevContent, content)) {
            return;
        }
        const parsedMentions = extractMentionsFromHtml(content);
        this.props.setPostData({ content, mentions: parsedMentions });
    }

    handleRelatedFeedbackClick = () => {
        const problemId = String(this.props.setPost?.postData?.problemId || '');
        const productId = String(this.props.setPost?.postData?.productId || '');
        if (!problemId) return;

        const params = new URLSearchParams({ problemId });
        if (productId) params.set('productId', productId);
        window.open(`/feedbackList?${params.toString()}`, '_blank');
    }

    checkDraft = async () => {
        const { setPost } = this.props;
        if (!setPost?.checkDraft) return;

        const ret = await setPost.checkDraft(2);
        if (ret.code === 0 && ret.data?.hasDraft) {
            this.setState({ hasDraft: true });
        }
    }

    handleRestoreDraft = async () => {
        const { setPost } = this.props;
        if (!setPost?.restoreDraft || !setPost?.draftData?.draftThreadId) {
            Toast.error('无法恢复草稿');
            return;
        }

        this.setState({ restoreDraftLoading: true });
        try {
            const ret = await setPost.restoreDraft(setPost.draftData.draftThreadId);
            if (ret.code === 0) {
                Toast.success('草稿已恢复');
                this.setState((prev) => ({
                    hasDraft: false,
                    editorInstanceKey: prev.editorInstanceKey + 1,
                }));
            } else {
                Toast.error(ret.message || '恢复草稿失败');
            }
        } catch (error) {
            Toast.error('恢复草稿出错');
        } finally {
            this.setState({ restoreDraftLoading: false });
        }
    }

    handleSaveDraft = async () => {
        const { setPost } = this.props;
        if (!setPost?.saveDraftManually) {
            Toast.error('缺少保存方法');
            return;
        }

        this.setState({ saveDraftLoading: true });
        try {
            const ret = await setPost.saveDraftManually(null, null, 2);
            if (ret.code === 0) {
                Toast.success('草稿已保存');
            } else {
                Toast.error(ret.message || '保存草稿失败');
            }
        } catch (error) {
            Toast.error('保存草稿出错');
        } finally {
            this.setState({ saveDraftLoading: false });
        }
    }

    hasValidEditorContent = (content: string) => {
        if (!content) return false;

        const cleanContent = content.replace(/<[^>]*>/g, '').trim();
        if (cleanContent) return true;

        if (/<img[^>]*>/i.test(content)) return true;
        if (/<video[^>]*>/i.test(content)) return true;
        if (/<audio[^>]*>/i.test(content)) return true;

        return false;
    }

    render() {
        const { setPost, editorReady = false } = this.props;
        const { title } = this.state;
        const displayTitle = setPost?.postData?.title !== undefined ? setPost.postData.title : title;
        const initialEditorContent = setPost?.postData?.content || '';
        const problemId = String(setPost?.postData?.problemId || '');
        const solvedCount = Number(setPost?.postData?.problemSolvedCount ?? 0) || 0;
        const hasContent = !!(
            this.hasValidEditorContent(setPost?.postData?.content || '') &&
            problemId &&
            setPost?.postData?.productId &&
            (setPost?.postData?.systemVersion || '').trim()
        );

        return (
            <div className={styles.wrapper}>
                <div className={styles.formContainer}>
                    {editorReady ? (
                        <FeedbackAttachmentProvider>
                            <TiptapEditor
                                key={`${setPost?.postData?.id || 'new'}-${this.state.editorInstanceKey}`}
                                initialContent={initialEditorContent}
                                onContentChange={this.handleContentChange}
                                emojiList={this.props.site?.emojiList}
                                solvedCount={solvedCount}
                                onRelatedClick={problemId && solvedCount > 0 ? this.handleRelatedFeedbackClick : undefined}
                            />
                            <FeedbackAttachmentDisplay />
                        </FeedbackAttachmentProvider>
                    ) : null}
                    <FeedbackFormFields />
                </div>
                <div style={{ height: '1px' }} />
                <div className={styles.fixedFooterBar}>
                    <div className={styles.fixedFooterBarContent}>
                        <div className={styles.footerBtnGroup}>
                            {(() => {
                                const isRestoredDraft = !!this.props.setPost?.draftData?.isRestoreDraft || !!this.props.setPost?.postData?.isDraft;
                                const isEditingPublishedPost = !!this.props.setPost?.postData?.id && !isRestoredDraft;
                                return isEditingPublishedPost ? null : (
                                    <>
                                        <button
                                            className={styles.outlineBtn}
                                            onClick={this.handleRestoreDraft}
                                            disabled={!this.state.hasDraft || this.state.restoreDraftLoading}
                                        >
                                            {this.state.restoreDraftLoading ? '恢复中...' : '恢复草稿'}
                                        </button>
                                        <button
                                            className={styles.outlineBtn}
                                            onClick={this.handleSaveDraft}
                                            disabled={this.state.saveDraftLoading}
                                        >
                                            {this.state.saveDraftLoading ? '保存中...' : '存草稿'}
                                        </button>
                                    </>
                                );
                            })()}
                        </div>
                        <div className={styles.footerBtnGroup}>
                            <button className={styles.outlineBtn} style={{ opacity: 0 }}>预览</button>
                            <button
                                className={styles.gradientBtn}
                                onClick={() => {
                                    if (!this.props.loading) {
                                        this.props.onPublishPost(null, null);
                                    }
                                }}
                                disabled={this.props.loading || !hasContent}
                            >
                                <span className={styles.gradientBtnText}>{this.props.loading ? '发布中...' : '发布'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default withRouter(SetFeedbackPostPC);
